// src/components/NodeDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DataTable from './DataTable';
import axios from 'axios';
import { saveAs } from 'file-saver';
import SensorVisualization from './SensorVisualization';

const NodeDetails = () => {
  const { nodeId } = useParams();
  const [timeRange, setTimeRange] = useState('1h');
  const [sensorData, setSensorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedSensors, setSelectedSensors] = useState([
    { value: 'temperature', label: 'Temperature', color: '#8884d8' },
    { value: 'relative_humidity', label: 'Humidity', color: '#82ca9d' },
    { value: 'pressure', label: 'Pressure', color: '#ffc658' },
  ]);

  const pluginVersion = {
    W026: '0.6.0.*',
    W027: '0.6.0.*',
    W068: '0.6.0.*',
    W07A: '0.6.0.*',
    W07B: '0.6.0.*',
    W07D: '0.6.0.*',
    W07E: '0.6.0.*',
    W020: '0.6.0.*',
  };

  const fetchSensorData = async () => {
    setLoading(true);
    try {
      const startMap = {
        '1h': '-1h',
        '1d': '-1d',
        '3d': '-3d',
      };
      const start = startMap[timeRange];

      const response = await axios.post('/api/proxy', {
        start,
        filter: {
          plugin: `waggle/plugin-iio:${pluginVersion[nodeId] || '0.7.0.*'}`,
          vsn: nodeId,
          name: 'env.pressure|env.relative_humidity|env.temperature',
        },
      });

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Group readings by timestamp
      const groupedReadings = {};

      response.data.forEach((reading) => {
        // Round timestamp to nearest second
        const timestamp = new Date(reading.timestamp).getTime();
        const roundedTime = Math.round(timestamp / 1000) * 1000;
        const roundedTimestamp = new Date(roundedTime).toISOString();

        if (!groupedReadings[roundedTimestamp]) {
          groupedReadings[roundedTimestamp] = {
            timestamp: roundedTimestamp,
            temperature: null,
            relative_humidity: null,
            pressure: null,
          };
        }

        const sensorType = reading.name.split('.').pop();
        switch (sensorType) {
          case 'temperature':
            groupedReadings[roundedTimestamp].temperature = reading.value;
            break;
          case 'relative_humidity':
            groupedReadings[roundedTimestamp].relative_humidity = reading.value;
            break;
          case 'pressure':
            groupedReadings[roundedTimestamp].pressure = reading.value;
            break;
          default:
            break;
        }
      });

      // Convert to array and filter incomplete readings
      const processedData = Object.values(groupedReadings)
        .filter(
          (reading) =>
            reading.temperature !== null &&
            reading.relative_humidity !== null &&
            reading.pressure !== null
        )
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setSensorData(processedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sensor data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSensorData();
    setCurrentPage(1);
  }, [nodeId, timeRange]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getCurrentPageData = () => {
    let sortedData = [...sensorData];

    if (sortField) {
      sortedData.sort((a, b) => {
        if (sortField === 'timestamp') {
          return sortDirection === 'asc'
            ? new Date(a.timestamp) - new Date(b.timestamp)
            : new Date(b.timestamp) - new Date(a.timestamp);
        }

        const aValue = a[sortField];
        const bValue = b[sortField];

        if (aValue === null) return sortDirection === 'asc' ? 1 : -1;
        if (bValue === null) return sortDirection === 'asc' ? -1 : 1;

        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }

    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedData = sortedData.slice(startIndex, startIndex + rowsPerPage);

    return paginatedData;
  };

  const handleExportCSV = () => {
    const headers = ['Timestamp', ...selectedSensors.map((sensor) => sensor.label)];
    const csvContent = [
      headers.join(','),
      ...sensorData.map((row) =>
        [
          new Date(row.timestamp).toLocaleString(),
          ...selectedSensors.map((sensor) => {
            const value = row[sensor.value];
            return value !== null ? value.toFixed(2) : 'N/A';
          }),
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `sensor_data_${nodeId}_${new Date().toISOString()}.csv`);
  };

  return (
    <div className="w-full px-4 py-6">
      <h2 className="text-2xl font-bold mb-6 text-center">Node Details: {nodeId}</h2>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-lg">Loading sensor data...</p>
        </div>
      ) : (
        <>
          <DataTable
            currentData={getCurrentPageData()}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(Number(e.target.value))}
            timeRange={timeRange}
            onTimeRangeChange={(e) => setTimeRange(e.target.value)}
            onExportCSV={handleExportCSV}
            onSort={handleSort}
            currentPage={currentPage}
            totalPages={Math.ceil(sensorData.length / rowsPerPage)}
            onPageChange={setCurrentPage}
            selectedSensors={selectedSensors}
            onSensorChange={setSelectedSensors}
          />

          <div
            className="grid gap-6 mt-6"
            style={{
              gridTemplateColumns: `repeat(${selectedSensors.length}, 1fr)`,
              justifyContent: 'center',
            }}
          >
            {selectedSensors.map((sensor) => (
              <div key={sensor.value} className="bg-white p-4 rounded-lg shadow-lg">
                <h3 className="text-xl font-semibold mb-4 text-center">{sensor.label}</h3>
                <SensorVisualization
                  data={sensorData}
                  sensor={sensor}
                  timeRange={timeRange}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default NodeDetails;